import { useState } from "react";
import { FaUtensils } from "react-icons/fa";
import { FiGrid, FiInfo, FiSearch, FiShoppingBag } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { categoryIconsMap } from "../../../config/categoryIcons";
import { formatPersianPrice, getCategoryKey, parseTableNumber } from "../../../services/customerMenuModel";
import MenuIntro from "../components/MenuIntro";
import MenuSplash from "../components/MenuSplash";
import SakuraBranch from "./japaneseMinimal/SakuraBranch";

const persianNumber = new Intl.NumberFormat("fa-IR");
const RED = "#B3231C";
const INK = "#1C1C1C";
const CREAM = "#F7F3EC";

const JapaneseItemCard = ({ item, quantity = 0, onAdd, onDecrease, onClick }) => (
  <article className="flex gap-3 border-b border-black/5 py-4">
    <button type="button" onClick={() => onClick(item)} className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-black/5 focus:outline-none focus:ring-2" style={{ "--tw-ring-color": RED }}>
      {item.image ? <img src={item.image} alt={item.name} className="h-full w-full object-cover" loading="lazy" /> : <span className="flex h-full w-full items-center justify-center text-2xl text-black/20"><FaUtensils /></span>}
    </button>
    <button type="button" onClick={() => onClick(item)} className="min-w-0 flex-1 text-right focus:outline-none">
      <h3 className="font-bold" style={{ color: INK }}>{item.name}</h3>
      {item.description && <p className="mt-1 line-clamp-1 text-xs text-black/50">{item.description}</p>}
      <p className="mt-2 text-sm font-black" style={{ color: RED }}>{formatPersianPrice(item.price)}</p>
    </button>
    <div className="flex shrink-0 items-center">
      {quantity > 0 ? (
        <div className="flex items-center gap-1.5 rounded-full border px-1 py-1" style={{ borderColor: RED }}>
          <button type="button" onClick={() => onDecrease(item)} aria-label={`کم کردن ${item.name}`} className="flex h-6 w-6 items-center justify-center text-sm" style={{ color: RED }}>−</button>
          <span className="w-4 text-center text-xs font-bold" style={{ color: INK }}>{persianNumber.format(quantity)}</span>
          <button type="button" onClick={() => onAdd(item)} aria-label={`اضافه کردن ${item.name}`} className="flex h-6 w-6 items-center justify-center text-sm" style={{ color: RED }}>+</button>
        </div>
      ) : (
        <button type="button" onClick={() => onAdd(item)} aria-label={`افزودن ${item.name} به سبد`} className="flex h-9 w-9 items-center justify-center rounded-full text-white shadow" style={{ backgroundColor: RED }}>+</button>
      )}
    </div>
  </article>
);

const JapaneseMinimalMenuTheme = (props) => {
  const {
    menuData, restaurant, settings, profileImage, coverImage, categories, visibleItems,
    activeCategoryKey, setActiveCategoryKey, searchTerm, setSearchTerm, quantities, cartSummary,
    addItem, decreaseItem, openItem, openCart, showBamizbanBadge,
  } = props;
  const [infoOpen, setInfoOpen] = useState(false);
  const location = useLocation();
  const tableNumber = parseTableNumber(location.search);

  return (
    <div className="min-h-screen pb-28" dir="rtl" style={{ backgroundColor: CREAM }}>
      <MenuSplash
        restaurantName={restaurant.name || menuData.title}
        profileImage={profileImage}
        backgroundColor={INK}
        accentColor={RED}
        tagline="سادگی و طعم اصیل"
        Decoration={SakuraBranch}
      />
      <section className="relative h-64 overflow-hidden sm:h-72" style={{ backgroundColor: INK }}>
        <img src={coverImage} alt="" className="h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.35), rgba(28,28,28,0.15) 55%, " + CREAM + ")" }} />
        <SakuraBranch className="pointer-events-none absolute -left-2 -top-2 h-24 w-20 text-white/50" />
        <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
          <button type="button" onClick={() => setInfoOpen(true)} aria-label="اطلاعات رستوران" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow" style={{ color: RED }}><FiInfo /></button>
          {tableNumber && <span className="rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold shadow" style={{ color: INK }}>میز {persianNumber.format(tableNumber)}</span>}
        </div>
        <div className="absolute inset-x-0 bottom-4 flex flex-col items-center text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-white text-2xl font-black text-white" style={{ backgroundColor: RED }} aria-hidden="true">和</span>
          <h1 className="mt-2 text-xl font-black text-white">{restaurant.name || menuData.title}</h1>
          {settings.welcomeText && <p className="mt-1 max-w-xs text-xs leading-6 text-white/85">{settings.welcomeText}</p>}
        </div>
      </section>

      <main className="mx-auto max-w-3xl px-4 pt-4">
        <div className="sticky top-0 z-30 mx-auto -mx-4 bg-[#F7F3EC]/95 px-4 pb-3 pt-1 backdrop-blur">
          <label className="relative block">
            <span className="sr-only">جست‌وجوی منو</span>
            <FiSearch className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-black/30" />
            <input type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="جست‌وجوی غذا" className="min-h-12 w-full rounded-full border border-black/10 bg-white py-3 pl-4 pr-11 text-sm outline-none focus:ring-2" style={{ "--tw-ring-color": RED }} />
          </label>

          <div className="mt-4 flex gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="دسته‌بندی‌های منو">
            <button type="button" aria-pressed={activeCategoryKey === "all"} onClick={() => setActiveCategoryKey("all")} className="flex shrink-0 flex-col items-center gap-1.5">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border text-lg" style={activeCategoryKey === "all" ? { backgroundColor: RED, color: "#fff", borderColor: RED } : { borderColor: "rgba(0,0,0,0.1)", color: INK }}><FiGrid /></span>
              <span className="text-[11px] font-bold" style={{ color: INK }}>همه</span>
            </button>
            {categories.map((category) => {
              const key = getCategoryKey(category);
              const Icon = categoryIconsMap[category.icon] || FaUtensils;
              const active = activeCategoryKey === key;
              return (
                <button key={key} type="button" aria-pressed={active} onClick={() => setActiveCategoryKey(key)} className="flex shrink-0 flex-col items-center gap-1.5">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border text-lg" style={active ? { backgroundColor: RED, color: "#fff", borderColor: RED } : { borderColor: "rgba(0,0,0,0.1)", color: INK }}><Icon /></span>
                  <span className="max-w-16 truncate text-[11px] font-bold" style={{ color: INK }}>{category.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {visibleItems.length ? (
          <div className="divide-y divide-black/5">
            {visibleItems.map((item) => (
              <JapaneseItemCard key={item._id} item={item} quantity={quantities[item._id]} onAdd={addItem} onDecrease={decreaseItem} onClick={openItem} />
            ))}
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-dashed border-black/10 bg-white/60 px-4 py-14 text-center">
            <FaUtensils className="mx-auto text-3xl text-black/20" aria-hidden="true" />
            <p className="mt-3 font-bold" style={{ color: INK }}>آیتمی پیدا نشد</p>
            <p className="mt-1 text-sm text-black/50">عبارت جست‌وجو یا دسته‌بندی را تغییر دهید.</p>
            {searchTerm && <button type="button" onClick={() => { setSearchTerm(""); setActiveCategoryKey("all"); }} className="mt-4 text-sm font-bold" style={{ color: RED }}>پاک کردن جست‌وجو</button>}
          </div>
        )}

        {showBamizbanBadge && <p className="pb-2 pt-6 text-center text-[11px] text-black/30">ساخته شده با <span className="font-bold text-black/40">بامیزبان</span></p>}
      </main>

      {cartSummary.itemCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 p-3" dir="rtl">
          <button type="button" onClick={openCart} className="mx-auto flex min-h-12 w-full max-w-xl items-center justify-between rounded-full px-5 font-bold text-white shadow-lg" style={{ backgroundColor: INK }}>
            <span className="flex items-center gap-2"><FiShoppingBag /> مشاهده سبد <span className="rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: RED }}>{persianNumber.format(cartSummary.itemCount)} آیتم</span></span>
            <span>{formatPersianPrice(cartSummary.total)}</span>
          </button>
        </div>
      )}

      <MenuIntro open={infoOpen} restaurantData={restaurant} profileImage={profileImage} onClose={() => setInfoOpen(false)} />
    </div>
  );
};

export default JapaneseMinimalMenuTheme;
